/**
 * Permission engine (M4). Per connection, per action class, TWO holds one of four levels:
 *
 *   observe  can see (reads run; writes are refused)
 *   prepare  can prepare (writes produce a draft artifact, nothing leaves)
 *   ask      asks first (writes wait for an approval card)
 *   auto     auto, within the person's rules (writes run when the limit holds, else ask)
 *
 * Levels change only in You → What TWO can do, never from chat. Limits are data, evaluated
 * here; the authority text on the approval card comes from this file in both languages.
 */

export type ActionKind =
  'calendar_read' | 'calendar_write' | 'mail_read' | 'mail_draft' | 'mail_send' | 'contacts_read';

export const ACTION_KINDS: readonly ActionKind[] = [
  'calendar_read',
  'calendar_write',
  'mail_read',
  'mail_draft',
  'mail_send',
  'contacts_read',
];

export type PermissionLevel = 'observe' | 'prepare' | 'ask' | 'auto';
export const PERMISSION_LEVELS: readonly PermissionLevel[] = ['observe', 'prepare', 'ask', 'auto'];

/** Local working hours; days are 0 = Sunday … 6 = Saturday. */
export interface WorkingHours {
  start: string;
  end: string;
  days: number[];
}

/** Limits written as data. Only meaningful for auto (and shown next to the level). */
export interface PermissionLimit {
  working_hours?: WorkingHours | undefined;
  no_new_external_attendees?: boolean | undefined;
}

export interface Permission {
  action: ActionKind;
  level: PermissionLevel;
  limit?: PermissionLimit | undefined;
}

/** Levels the app offers per action class (auto is not offered for mail_send in M4). */
export const LEVELS_OFFERED: Record<ActionKind, readonly PermissionLevel[]> = {
  calendar_read: ['observe'],
  mail_read: ['observe'],
  contacts_read: ['observe'],
  calendar_write: ['observe', 'prepare', 'ask', 'auto'],
  mail_draft: ['observe', 'prepare', 'ask', 'auto'],
  mail_send: ['observe', 'prepare', 'ask'],
};

/** Gulf working week by default: Sunday to Thursday, 08:00–18:00 local. */
export const DEFAULT_WORKING_HOURS: WorkingHours = {
  start: '08:00',
  end: '18:00',
  days: [0, 1, 2, 3, 4],
};

/** The limit that applies when a class is set to auto and no limit was written. */
export function defaultLimit(action: ActionKind): PermissionLimit | undefined {
  if (action === 'calendar_write') {
    return { working_hours: DEFAULT_WORKING_HOURS, no_new_external_attendees: true };
  }
  return undefined;
}

/**
 * Defaults at connect: everything observe, mail_draft prepare, calendar_write and mail_send ask
 * (M4.7: calendar writes go through the card; a prepared proposal is not an event).
 */
export function defaultPermissions(): Permission[] {
  return ACTION_KINDS.map((action) => ({
    action,
    level:
      action === 'mail_draft'
        ? 'prepare'
        : action === 'calendar_write' || action === 'mail_send'
          ? 'ask'
          : 'observe',
  }));
}

export function isReadAction(action: ActionKind): boolean {
  return action === 'calendar_read' || action === 'mail_read' || action === 'contacts_read';
}

/** Whether a level may be set for a class (the engine and the PATCH route both check). */
export function isLevelOffered(action: ActionKind, level: PermissionLevel): boolean {
  return LEVELS_OFFERED[action].includes(level);
}

/** What the tool proposes, as far as the limits care. */
export interface ActionProposal {
  action: ActionKind;
  /** calendar_write: the event's start / end (ISO) and the person's zone. */
  start?: string | undefined;
  end?: string | undefined;
  timezone?: string | undefined;
  /** calendar_write: attendee emails that are not already on the event. */
  new_attendees?: readonly string[] | undefined;
  /** The person's own addresses (attendees at these domains are not external). */
  own_addresses?: readonly string[] | undefined;
  /** mail_send / mail_draft: recipients (informational). */
  recipients?: readonly string[] | undefined;
}

export type PermissionOutcome = 'allow' | 'prepare_only' | 'needs_approval' | 'denied';

export interface LocalizedText {
  en: string;
  ar: string;
}

export const AUTHORITY_TEXT = {
  requires_approval: { en: 'Requires your approval', ar: 'تحتاج موافقتك' },
  within_working_hours: { en: 'Within your rules: working hours', ar: 'ضمن قواعدك: ساعات العمل' },
  within_rules: { en: 'Within your rules', ar: 'ضمن قواعدك' },
  prepare_only: { en: 'Within your rules: prepare only', ar: 'ضمن قواعدك: الإعداد فقط' },
  observe_only: { en: 'Outside your rules: TWO can only see', ar: 'خارج قواعدك: TWO يرى فقط' },
  outside_hours: {
    en: 'Requires your approval: outside working hours',
    ar: 'تحتاج موافقتك: خارج ساعات العمل',
  },
  new_attendee: { en: 'Requires your approval: new attendee', ar: 'تحتاج موافقتك: مدعو جديد' },
  can_see: { en: 'Within your rules: can see', ar: 'ضمن قواعدك: يرى' },
} as const satisfies Record<string, LocalizedText>;

export type AuthorityKey = keyof typeof AUTHORITY_TEXT;

export interface PermissionDecision {
  outcome: PermissionOutcome;
  level: PermissionLevel;
  authority: AuthorityKey;
  /** Machine-readable reason, e.g. "level=ask", "outside_working_hours", "new_attendee". */
  reason: string;
  limit?: PermissionLimit | undefined;
}

/** Local weekday and HH:MM of an instant in a zone (Intl, no library). */
export function localParts(iso: string, timezone: string): { day: number; hhmm: string } | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const day = days.indexOf(get('weekday'));
    if (day < 0) return null;
    return { day, hhmm: `${get('hour')}:${get('minute')}` };
  } catch {
    return null;
  }
}

/** True when both ends of the span fall inside the working hours on a working day. */
export function withinWorkingHours(
  span: { start: string; end: string },
  hours: WorkingHours,
  timezone: string,
): boolean {
  const a = localParts(span.start, timezone);
  const b = localParts(span.end, timezone);
  if (!a || !b) return false;
  if (!hours.days.includes(a.day) || a.day !== b.day) return false;
  return a.hhmm >= hours.start && b.hhmm <= hours.end && a.hhmm < b.hhmm;
}

function domainOf(email: string): string {
  return email.trim().toLowerCase().split('@')[1] ?? '';
}

/** External = outside every domain of the person's own connected addresses. */
export function externalAttendees(
  attendees: readonly string[],
  ownAddresses: readonly string[],
): string[] {
  const own = new Set(ownAddresses.map(domainOf).filter(Boolean));
  const ownFull = new Set(ownAddresses.map((a) => a.trim().toLowerCase()));
  return attendees.filter((a) => !ownFull.has(a.trim().toLowerCase()) && !own.has(domainOf(a)));
}

/**
 * The engine: one decision for one proposed action under one permission. Reads run under
 * observe. Writes: observe → denied, prepare → prepare_only, ask → needs_approval, auto →
 * allow when the limit holds (else needs_approval with the reason in the authority).
 */
export function evaluatePermission(
  permission: Permission,
  proposal: ActionProposal,
  ctx: { timezone?: string | undefined } = {},
): PermissionDecision {
  const { level } = permission;
  const action = proposal.action;
  if (isReadAction(action)) {
    return { outcome: 'allow', level, authority: 'can_see', reason: 'read' };
  }
  if (level === 'observe') {
    return { outcome: 'denied', level, authority: 'observe_only', reason: 'level=observe' };
  }
  if (level === 'prepare') {
    return { outcome: 'prepare_only', level, authority: 'prepare_only', reason: 'level=prepare' };
  }
  if (level === 'ask' || !isLevelOffered(action, 'auto')) {
    return {
      outcome: 'needs_approval',
      level,
      authority: 'requires_approval',
      reason: level === 'ask' ? 'level=ask' : 'auto_not_offered',
    };
  }
  // auto: the limit decides.
  const limit = permission.limit ?? defaultLimit(action);
  if (action === 'calendar_write') {
    const hours = limit?.working_hours;
    if (hours) {
      if (!proposal.start || !proposal.end) {
        return {
          outcome: 'needs_approval',
          level,
          authority: 'requires_approval',
          reason: 'no_time_span',
          limit,
        };
      }
      const tz = proposal.timezone ?? ctx.timezone ?? 'Asia/Qatar';
      if (!withinWorkingHours({ start: proposal.start, end: proposal.end }, hours, tz)) {
        return {
          outcome: 'needs_approval',
          level,
          authority: 'outside_hours',
          reason: 'outside_working_hours',
          limit,
        };
      }
    }
    if (limit?.no_new_external_attendees && proposal.new_attendees?.length) {
      const external = externalAttendees(proposal.new_attendees, proposal.own_addresses ?? []);
      if (external.length > 0) {
        return {
          outcome: 'needs_approval',
          level,
          authority: 'new_attendee',
          reason: 'new_external_attendee',
          limit,
        };
      }
    }
    return {
      outcome: 'allow',
      level,
      authority: hours ? 'within_working_hours' : 'within_rules',
      reason: 'auto_within_limit',
      limit,
    };
  }
  return { outcome: 'allow', level, authority: 'within_rules', reason: 'auto', limit };
}

/** The permission for an action from a stored list, falling back to the connect defaults. */
export function permissionFor(list: readonly Permission[], action: ActionKind): Permission {
  return (
    list.find((p) => p.action === action) ??
    (defaultPermissions().find((p) => p.action === action) as Permission)
  );
}
